import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { ProjectOverviewLoadSystems } from '../core/store/actions/project-overview.actions';
import { getCurrentProject } from '@completion/selectors';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { ProjectsLoad } from '@completion/actions';
import { getProjectOverviewLoaded, getProjectOverviewProgress } from '../core/store/selectors/project-overview.selectors';

interface OverviewProgressVm {
  total: number;
  loaded: number;
  percent: number;
}

@Component({
  selector: 'project-overview',
  standalone: false,
  templateUrl: './project-overview.component.html',
  styleUrls: ['./project-overview.component.scss'],
})
export class ProjectOverviewComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  progress$: Observable<OverviewProgressVm>;
  loaded$: Observable<boolean>;

  showProgressBar = false;
  private hideTimeoutId: any = null;

  constructor(private store: Store) {}

  ngOnInit(): void {
    this.progress$ = this.store.select(getProjectOverviewProgress) as Observable<OverviewProgressVm>;
    this.loaded$ = this.store.select(getProjectOverviewLoaded);

    // Single subscription: log and control progress bar visibility
    this.progress$.pipe(takeUntil(this.destroy$)).subscribe((progress) => {
      if (!progress) {
        this.clearHideTimeout();
        this.showProgressBar = false;
        return;
      }

      if (progress.percent < 100) {
        // While loading, ensure bar is visible and cancel any pending hide
        this.clearHideTimeout();
        this.showProgressBar = true;
      } else {
        // We have reached 100%. If we’re already in a completed state and the bar is hidden,
        // do nothing. If it’s visible, schedule a one-time hide.
        if (!this.showProgressBar) {
          return;
        }
        if (!this.hideTimeoutId) {
          this.hideTimeoutId = setTimeout(() => {
            this.showProgressBar = false;
            this.clearHideTimeout();
          }, 2000);
        }
      }
    });

    // Only dispatch LoadSystems when the project ID actually changes
    this.store
      .select(getCurrentProject)
      .pipe(
        map((p) => (p ? p.id : null)),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((projectId) => {
        if (projectId) {
          this.store.dispatch(new ProjectOverviewLoadSystems());
        } else {
          this.store.dispatch(new ProjectsLoad());
        }
      });
  }

  private clearHideTimeout(): void {
    if (this.hideTimeoutId) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }
  }

  ngOnDestroy(): void {
    this.clearHideTimeout();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
